/**
 * AYU FAQ-Chat-Widget
 * 
 * Zwei Modi:
 * 1. "homepage" — Floating Bubble unten rechts, Funnel-Trichter
 * 2. "app" — Eingebettet in die Hilfe-Seite, voller Helfer
 */
import { useState, useRef, useEffect, useCallback } from "react";
import { trpc } from "@/lib/trpc";
import { MessageCircle, X, Send, Loader2, ChevronDown, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Streamdown } from "streamdown";

import { createLogger } from "@/lib/logger";
const log = createLogger("Faqchatwidget");

interface FaqChatWidgetProps {
  mode: "homepage" | "app" | "app-floating";
  clientId?: string;
  clientName?: string;
}

interface ChatMessage {
  id: number;
  role: "user" | "assistant";
  content: string;
  quickReplies?: string[];
}

export default function FaqChatWidget({ mode, clientId, clientName }: FaqChatWidgetProps) {
  const [isOpen, setIsOpen] = useState(mode === "app");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [conversationId, setConversationId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const userScrolledUpRef = useRef(false);

  const startMutation = trpc.faq.startConversation.useMutation();
  const sendMutation = trpc.faq.sendMessage.useMutation();

  // app-floating uses "app" botType for the same knowledge base
  const botType = mode === "app-floating" ? "app" : mode;

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  // Detect user scroll: pause auto-scroll when user scrolls up
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 60;
      userScrolledUpRef.current = !isAtBottom;
    };
    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  // Auto-scroll only if user hasn't scrolled up
  useEffect(() => {
    if (!userScrolledUpRef.current) {
      scrollToBottom();
    }
  }, [messages, scrollToBottom]);

  // Start conversation when widget opens
  const startChat = useCallback(async () => {
    if (hasStarted) return;
    setHasStarted(true);
    setIsLoading(true);
    try {
      const result = await startMutation.mutateAsync({
        botType: botType as "homepage" | "app",
        visitorId: mode === "homepage" ? `visitor_${Date.now()}_${Math.random().toString(36).slice(2)}` : undefined,
        clientId: clientId,
        name: clientName,
        startPage: window.location.pathname,
      });
      setConversationId(result.conversationId);
      setMessages([{
        id: 1,
        role: "assistant",
        content: result.greeting.text,
        quickReplies: result.greeting.quickReplies,
      }]);
    } catch (err) {
      log.error("Failed to start:");
      setMessages([{
        id: 1,
        role: "assistant",
        content: "Entschuldigung, ich konnte den Chat nicht starten. Bitte versuche es später erneut.",
        quickReplies: [],
      }]);
    } finally {
      setIsLoading(false);
    }
  }, [hasStarted, mode, clientId, clientName, startMutation]);

  useEffect(() => {
    if (isOpen && !hasStarted) {
      startChat();
    }
  }, [isOpen, hasStarted, startChat]);

  const handleSend = async (text?: string) => {
    const messageText = text || input.trim();
    if (!messageText || !conversationId || isLoading) return;

    setInput("");
    const userMsg: ChatMessage = {
      id: messages.length + 1,
      role: "user",
      content: messageText,
    };
    setMessages((prev) => [...prev, userMsg]);
    setIsLoading(true);

    try {
      const result = await sendMutation.mutateAsync({
        conversationId,
        message: messageText,
        botType: botType as "homepage" | "app",
        clientName: clientName,
      });
      setMessages((prev) => [
        ...prev,
        {
          id: prev.length + 1,
          role: "assistant",
          content: result.text,
          quickReplies: result.quickReplies,
        },
      ]);
    } catch (err) {
      log.error("Send error:");
      setMessages((prev) => [
        ...prev,
        {
          id: prev.length + 1,
          role: "assistant",
          content: "Entschuldigung, etwas ist schiefgelaufen. Bitte versuche es erneut.",
          quickReplies: [],
        },
      ]);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ─── Embedded mode (App) ───
  if (mode === "app") {
    return (
      <div className="flex flex-col h-full bg-white rounded-xl border border-[#e8e0d4] overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-[#e8e0d4] bg-gradient-to-r from-[#f5f0e8] to-[#f0ebe0]">
          <div className="w-10 h-10 rounded-full bg-[#5a6b3c] flex items-center justify-center">
            <MessageCircle className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-[#2d3a1a] text-base">AYU Hilfe-Assistent</h3>
            <p className="text-xs text-[#6b7c5a]">Frag mich alles rund um AYU</p>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((msg) => (
            <div key={msg.id} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                  msg.role === "user"
                    ? "bg-[#5a6b3c] text-white rounded-br-md"
                    : "bg-[#f5f0e8] text-[#2d3a1a] rounded-bl-md"
                }`}
              >
                <Streamdown
                  components={{
                    a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
                      <a href={href} target="_blank" rel="noopener noreferrer" className="text-[#6b7f4e] underline font-medium hover:text-[#5a6e3f]">{children}</a>
                    ),
                  }}
                >{msg.content}</Streamdown>
              </div>
            </div>
          ))}
          {/* Quick Replies */}
          {messages.length > 0 && messages[messages.length - 1].quickReplies && messages[messages.length - 1].quickReplies!.length > 0 && !isLoading && (
            <div className="flex flex-wrap gap-2 pl-2">
              {messages[messages.length - 1].quickReplies!.map((reply, i) => (
                <button
                  key={i}
                  onClick={() => handleSend(reply)}
                  className="text-xs px-3 py-1.5 rounded-full border border-[#5a6b3c]/30 text-[#5a6b3c] hover:bg-[#5a6b3c]/10 transition-colors"
                >
                  {reply}
                </button>
              ))}
            </div>
          )}
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-[#f5f0e8] rounded-2xl rounded-bl-md px-4 py-3">
                <div className="flex gap-1.5">
                  <span className="w-2 h-2 bg-[#5a6b3c]/40 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-2 h-2 bg-[#5a6b3c]/40 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-2 h-2 bg-[#5a6b3c]/40 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="border-t border-[#e8e0d4] p-3 bg-white">
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Deine Frage..."
              className="flex-1 min-w-0 px-4 py-2.5 rounded-full border border-[#e8e0d4] bg-[#faf8f4] text-sm text-[#2d3a1a] placeholder:text-[#a09880] focus:outline-none focus:ring-2 focus:ring-[#5a6b3c]/20 focus:border-[#5a6b3c]/40"
              disabled={isLoading}
            />
            <Button
              size="icon"
              onClick={() => handleSend()}
              disabled={!input.trim() || isLoading}
              className="shrink-0 w-10 h-10 rounded-full bg-[#5a6b3c] hover:bg-[#4a5b2c] text-white disabled:opacity-40"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ─── App Floating mode ───
  if (mode === "app-floating") {
    return (
      <>
        {/* Floating Help Bubble - right side, above bug report */}
        {!isOpen && (
          <button
            onClick={() => setIsOpen(true)}
            className="fixed bottom-20 left-6 z-50 w-12 h-12 rounded-full bg-[#5a6b3c] text-white shadow-lg hover:bg-[#4a5b2c] transition-all hover:scale-105 flex items-center justify-center"
            aria-label="Hilfe-Chat öffnen"
            title="Hilfe-Assistent"
          >
            <HelpCircle className="w-5 h-5" />
          </button>
        )}

        {/* Chat Window */}
        {isOpen && (
          <div className="fixed bottom-6 left-6 z-50 w-[360px] max-w-[calc(100vw-2rem)] h-[500px] max-h-[calc(100vh-6rem)] flex flex-col bg-white rounded-2xl shadow-2xl border border-[#e8e0d4] overflow-hidden animate-in slide-in-from-bottom-4 fade-in duration-300">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#e8e0d4] bg-gradient-to-r from-[#5a6b3c] to-[#4a5b2c]">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                  <HelpCircle className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-white text-sm">AYU Hilfe-Assistent</h3>
                  <p className="text-[10px] text-white/70">Frag mich alles rund um AYU</p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors"
                aria-label="Chat schließen"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>

            {/* Messages */}
            <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-3 space-y-3">
              {messages.map((msg) => (
                <div key={msg.id} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed ${
                      msg.role === "user"
                        ? "bg-[#5a6b3c] text-white rounded-br-md"
                        : "bg-[#f5f0e8] text-[#2d3a1a] rounded-bl-md"
                    }`}
                  >
                    <Streamdown
                      components={{
                        a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
                          <a href={href} target="_blank" rel="noopener noreferrer" className="text-[#6b7f4e] underline font-medium hover:text-[#5a6e3f]">{children}</a>
                        ),
                      }}
                    >{msg.content}</Streamdown>
                  </div>
                </div>
              ))}
              {/* Quick Replies */}
              {messages.length > 0 && messages[messages.length - 1].quickReplies && messages[messages.length - 1].quickReplies!.length > 0 && !isLoading && (
                <div className="flex flex-wrap gap-1.5 pl-1">
                  {messages[messages.length - 1].quickReplies!.map((reply, i) => (
                    <button
                      key={i}
                      onClick={() => handleSend(reply)}
                      className="text-xs px-3 py-1.5 rounded-full border border-[#5a6b3c]/30 text-[#5a6b3c] hover:bg-[#5a6b3c]/10 transition-colors"
                    >
                      {reply}
                    </button>
                  ))}
                </div>
              )}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-[#f5f0e8] rounded-2xl rounded-bl-md px-3.5 py-2.5">
                    <div className="flex gap-1.5">
                      <span className="w-1.5 h-1.5 bg-[#5a6b3c]/40 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                      <span className="w-1.5 h-1.5 bg-[#5a6b3c]/40 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                      <span className="w-1.5 h-1.5 bg-[#5a6b3c]/40 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="border-t border-[#e8e0d4] p-2.5 bg-white">
              <div className="flex items-center gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Deine Frage..."
                  className="flex-1 min-w-0 px-3.5 py-2 rounded-full border border-[#e8e0d4] bg-[#faf8f4] text-[13px] text-[#2d3a1a] placeholder:text-[#a09880] focus:outline-none focus:ring-2 focus:ring-[#5a6b3c]/20 focus:border-[#5a6b3c]/40"
                  disabled={isLoading}
                />
                <Button
                  size="icon"
                  onClick={() => handleSend()}
                  disabled={!input.trim() || isLoading}
                  className="shrink-0 w-9 h-9 rounded-full bg-[#5a6b3c] hover:bg-[#4a5b2c] text-white disabled:opacity-40"
                >
                  <Send className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // ─── Floating mode (Homepage) ───
  return (
    <>
      {/* Floating Bubble */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#5a6b3c] text-white shadow-lg hover:bg-[#4a5b2c] transition-all hover:scale-105 flex items-center justify-center group"
          aria-label="Chat öffnen"
        >
          <MessageCircle className="w-6 h-6" />
          {/* Pulse animation */}
          <span className="absolute inset-0 rounded-full bg-[#5a6b3c] animate-ping opacity-20" />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 z-50 w-[360px] max-w-[calc(100vw-2rem)] h-[500px] max-h-[calc(100vh-6rem)] flex flex-col bg-white rounded-2xl shadow-2xl border border-[#e8e0d4] overflow-hidden animate-in slide-in-from-bottom-4 fade-in duration-300">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#e8e0d4] bg-gradient-to-r from-[#5a6b3c] to-[#4a5b2c]">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <MessageCircle className="w-4 h-4 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-white text-sm">AYU Assistent</h3>
                <p className="text-[10px] text-white/70">Wie kann ich dir helfen?</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors"
              aria-label="Chat schließen"
            >
              <X className="w-4 h-4 text-white" />
            </button>
          </div>

          {/* Messages */}
          <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-3 space-y-3">
            {messages.map((msg) => (
              <div key={msg.id} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed ${
                    msg.role === "user"
                      ? "bg-[#5a6b3c] text-white rounded-br-md"
                      : "bg-[#f5f0e8] text-[#2d3a1a] rounded-bl-md"
                  }`}
                >
                  <Streamdown
                    components={{
                      a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
                        <a href={href} target="_blank" rel="noopener noreferrer" className="text-[#6b7f4e] underline font-medium hover:text-[#5a6e3f]">{children}</a>
                      ),
                    }}
                  >{msg.content}</Streamdown>
                </div>
              </div>
            ))}
            {/* Quick Replies */}
            {messages.length > 0 && messages[messages.length - 1].quickReplies && messages[messages.length - 1].quickReplies!.length > 0 && !isLoading && (
              <div className="flex flex-wrap gap-1.5 pl-1">
                {messages[messages.length - 1].quickReplies!.map((reply, i) => (
                  <button
                    key={i}
                    onClick={() => handleSend(reply)}
                    className="text-xs px-3 py-1.5 rounded-full border border-[#5a6b3c]/30 text-[#5a6b3c] hover:bg-[#5a6b3c]/10 transition-colors"
                  >
                    {reply}
                  </button>
                ))}
              </div>
            )}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-[#f5f0e8] rounded-2xl rounded-bl-md px-3.5 py-2.5">
                  <div className="flex gap-1.5">
                    <span className="w-1.5 h-1.5 bg-[#5a6b3c]/40 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-1.5 h-1.5 bg-[#5a6b3c]/40 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-1.5 h-1.5 bg-[#5a6b3c]/40 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t border-[#e8e0d4] p-2.5 bg-white">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Deine Frage..."
                className="flex-1 min-w-0 px-3.5 py-2 rounded-full border border-[#e8e0d4] bg-[#faf8f4] text-[13px] text-[#2d3a1a] placeholder:text-[#a09880] focus:outline-none focus:ring-2 focus:ring-[#5a6b3c]/20 focus:border-[#5a6b3c]/40"
                disabled={isLoading}
              />
              <Button
                size="icon"
                onClick={() => handleSend()}
                disabled={!input.trim() || isLoading}
                className="shrink-0 w-9 h-9 rounded-full bg-[#5a6b3c] hover:bg-[#4a5b2c] text-white disabled:opacity-40"
              >
                <Send className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
